/**
 * phoneNumberFormatter.js
 * Talkroute
 *
 * Created by Vel Genov on 5/23/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */
import { AsYouType, isPossiblePhoneNumber, isValidNumber, parsePhoneNumber } from "libphonenumber-js";
import { COUNTRY_CODE } from "../../services/dataClasses/TalkroutePhoneNumber";

const US_NUMBER_FORMAT = "US";

export const addPhoneNumberPlusSign = (phoneNumber: string) => {
  if (phoneNumber === "") return phoneNumber;
  return phoneNumber.startsWith("+") ? phoneNumber : "+" + phoneNumber;
}

export const trimPhoneNumberPlusSign = (phoneNumber: string) => {
  if (!phoneNumber || !phoneNumber.length) return "";
  return phoneNumber.startsWith("+") ? phoneNumber.slice(1) : phoneNumber;
};

/**
 * @param inputPhoneNumber phone with or without + sign
 * @returns formatted phone number if national will return format +1 (xxx) xxx-xxxx
 * if international will return format +xx xxx xxx xxx
 */
export const formatBothNationalInternational = (inputPhoneNumber: string) => {
  let formattedNumber = trimPhoneNumberPlusSign(inputPhoneNumber)
  if (formattedNumber.startsWith("1")) {
    formattedNumber = "+" + formatPhoneNumber(formattedNumber)
  } else {
    formattedNumber = formatNumberInternational(formattedNumber)
  }
  return formattedNumber
}

/**
 * Used in only one special case. Should be removed in the future when 
 * deciding on formatting standards
 */
export const formatBothNationalInternationalSpecial = (inputPhoneNumber: string) => {
  let formattedNumber = trimPhoneNumberPlusSign(inputPhoneNumber)
  if (formattedNumber.startsWith("1")) {
    formattedNumber = "+" + formatPhoneNumber(formattedNumber)
  } else {
    formattedNumber = new AsYouType().input(addPhoneNumberPlusSign(inputPhoneNumber));
  }
  return formattedNumber
}

export const formatNumberToE164 = (inputPhoneNumber: string) => {
  try {
    const parsedNumber = parsePhoneNumber(inputPhoneNumber, "US");
    return parsedNumber.format("E.164");
  } catch (error) {
    throw error
  }
}
/**
 * @param inputPhoneNumber formatted or unformatted phone number
 * @returns attempts to format as international or nationalthe number,
 * with a bias for the US national format.
 * See formatNumberAsYouType.spec.ts for tests
 */
export const formatNumberAsYouType = (inputPhoneNumber: string) => {
  if (inputPhoneNumber.startsWith("+")) {
    if ((inputPhoneNumber.length > 5 || inputPhoneNumber.length >= 14) && (inputPhoneNumber.startsWith("+1") === false)) {
      const processedNumber = new AsYouType().input(inputPhoneNumber);
      if (isPossiblePhoneNumber(processedNumber)) {
        const phoneNumber = parsePhoneNumber(processedNumber);
        return phoneNumber.format("INTERNATIONAL");
      }
      return processedNumber;
    }

    if (inputPhoneNumber.length > 5 || inputPhoneNumber.length >= 14 && inputPhoneNumber.startsWith("+1")) {
      return new AsYouType("US").input(inputPhoneNumber.slice(2));
    }

    else {
      const processedNumber = new AsYouType().input(inputPhoneNumber);
      if (isPossiblePhoneNumber(processedNumber)) {
        const phoneNumber = parsePhoneNumber(processedNumber);
        return phoneNumber.format("INTERNATIONAL");
      }
      return processedNumber;
    }
  } else {
    if ((inputPhoneNumber.length > 5 || inputPhoneNumber.length >= 14) && inputPhoneNumber.startsWith("1")) {
      return new AsYouType("US").input(inputPhoneNumber.slice(1));
    }
    if ((inputPhoneNumber.length === 16 || inputPhoneNumber.length === 11) && inputPhoneNumber.startsWith("1")) {
      return new AsYouType("US").input(inputPhoneNumber.slice(1));
    }
    if ((inputPhoneNumber.startsWith("(") && inputPhoneNumber.length > 15) || (inputPhoneNumber.length > 11 && !inputPhoneNumber.startsWith("("))) {
      return new AsYouType("US").input("+" + inputPhoneNumber);
    }
    if (inputPhoneNumber.startsWith("(") && inputPhoneNumber.length === 4) {
      return inputPhoneNumber
    }
    if (inputPhoneNumber.startsWith('1') === false) {
      const processedNumber = new AsYouType().input(inputPhoneNumber);
      if (isPossiblePhoneNumber(processedNumber)) {
        const phoneNumber = parsePhoneNumber(processedNumber);
        return phoneNumber.format("INTERNATIONAL");
      }
    }


    const processedNumber = new AsYouType("US").input(inputPhoneNumber);
    return processedNumber
  }
}

/**
 * Attempts to format the input phone number as a US phone number
 *
 * @param inputPhoneNumber the phone number to format
 * @returns the formatted phone number
 */
export const formatPhoneNumber = (inputPhoneNumber) => {
  const formattedNumber = new AsYouType(US_NUMBER_FORMAT).input(
    inputPhoneNumber
  );

  if (formattedNumber.endsWith(")")) {
    const resFormattedNumber = formattedNumber.replace(")", "");
    return resFormattedNumber;
  }

  return formattedNumber;
};

export const formatNumberInternational = (inputPhoneNumber: string) =>
  new AsYouType().input(inputPhoneNumber);


/**
* Simplify number format
* @param phoneNumber string
* @returns number without `(`, `)` and spaces, or null incase of bad input
*/
export const trimPhoneNumber = (phoneNumber: string) =>
  phoneNumber.replace(/\D/g, "").trim();

/**
* Add a leading COUNTRY_CODE to the phone number, if necessary
*
* @param {} phoneNumber the phone number to modify
*/
export const addLeadingCountryCode = (phoneNumber: string) => {
  if (phoneNumber === "") {
    return phoneNumber;
  }

  const updatedPhoneNumber =
    phoneNumber.startsWith(COUNTRY_CODE)
      ? phoneNumber
      : `${COUNTRY_CODE}${phoneNumber}`;
  return updatedPhoneNumber;
};

/**
 * Check if a phone number is a non US one
 */
export const isPhoneNumberInternational = (phoneNumber: string) => parsePhoneNumber(phoneNumber).country !== "US"
